import { IHttpInterceptor, IHttpResponse, IHttpService, ILogService, IPromise, IQService, IRequestConfig } from "angular";
import { $InjectorLike } from "angular-ui-router";
import { ApiClientService } from "../services/api-client.service";
import { containsInvariant } from "../services/filter-util";
import { SessionService } from "../services/session.service";
import { HttpInterceptorFactory } from "./http-interceptor.factory";

const unauthorizedStatus = { status: 401, statusName: "Unauthorized" };

/**
 * An Http interceptor that adds the user's Apex Access Token from the session to
 * all requests being sent to the API.
 */
export class ApexAuthInterceptor implements IHttpInterceptor {
    /**
     * Create a new ApexAuthInterceptor.
     * @param $log The AngularJS logging service.
     * @param $q The AngularJS promise service.
     * @param $injector The AngularJS injector service used to lazy-inject dependencies that use $http.
     */
    constructor(private $log: ILogService, private $q: IQService, private $injector: $InjectorLike) {
        'ngInject'
    }


    // Use arrow function syntax to avoid losing bindings to object context.
    request(config: IRequestConfig) {
        // Strip trailing slash(es) from the URL used for comparison.
        const apexApiUrl = this.$injector.get("apexApiUrl").replace(/\/+$/, "");
        if ((config as any).disableApexAuthInterceptor) {
            // Allow $http requests to opt-out of injecting the access token.
            return this.$q.resolve(config);
        } else if (!containsInvariant(config.url, apexApiUrl)) {
            // Only attach the access token to API-bound requests.
            return this.$q.resolve(config);
        }

        const apiClientService: ApiClientService = this.$injector.get("apiClientService");
        const sessionService: SessionService = this.$injector.get("sessionService");
        const addJwtAuthentication = () => {
            return apiClientService.retrieveAccessToken()
                .then(token => {
                    config.headers.Authorization = "Bearer " + token;
                    return config;
                })
                .catch(ex => {
                    if (ex.status === 401) {
                        // The rejection will be handled, but log the original error
                        // and indicate failure by throwing the unauthorized status error.
                        this.$log.info("Request for access token returned 401 Unauthorized", ex);
                        return this.signOut().then(() => {
                            throw unauthorizedStatus
                        });
                    }
                    throw ex;
                });
        };
        return sessionService.isAuthorized()
            .then(isAuthorized => {
                if (isAuthorized) {
                    return addJwtAuthentication();
                }
                return config;
            });
    }

    private signOut() {
        const sessionService = this.$injector.get<SessionService>("sessionService");
        if (sessionService.isAuthorized()) {
            // Only sign out if the user is in a session. Otherwise, this could result in
            // running the user through a loop of reloading the page.
            return sessionService.signOut();
        }
    }

    private retryWithNewerToken<T>(rejection: IHttpResponse<T>): IPromise<IHttpResponse<T>> {
        const apiClientService: ApiClientService = this.$injector.get("apiClientService");
        const $http = this.$injector.get<IHttpService>("$http");
        const originalAuth = rejection.config.headers.Authorization;
        return apiClientService.retrieveAccessToken()
            .then(token => {
                const auth = `Bearer ${token}`;
                if (auth !== originalAuth) {
                    // Retry the request.
                    const config = { ...rejection.config };
                    config.headers.Authorization = auth;
                    return $http<T>(config);
                }
                const [, payload] = token.split(".");
                const claims = JSON.parse(atob(payload));
                this.$log.warn("Auth token was not accepted for authentication.", claims, rejection.config);
                // Otherwise, the rejection still stands.
                throw rejection;
            }, () => {
                // Upon failure to obtain the new access token itself, rethrow the original rejection.
                throw rejection
            });
    }

    responseError<T>(rejection: IHttpResponse<T>): IPromise<IHttpResponse<T>> | IHttpResponse<T> {
        if (rejection.status === 401 && !(rejection.config as any).disableApexAuthInterceptor) {
            this.$log.info("Endpoint returned 401 Unauthorized", rejection.config.url);
            return this.retryWithNewerToken(rejection);
        } else {
            throw rejection;
        }
    }
}

export const apexAuthInterceptorFactory = HttpInterceptorFactory(ApexAuthInterceptor);
